import type {
  Cut,
  CutsheetCatalog,
  Extra,
  Specification,
  SpecificationOption,
  Trim,
} from '../../types';

/**
 * Returns the stored value for a spec option (rich object or plain string).
 * @param option
 */
export const getOptionValue = (option: string | SpecificationOption): string =>
  typeof option === 'string'
    ? option
    : (option.value ?? option.label ?? option.title ?? '');

/**
 * Returns the display label for a spec option (rich object or plain string).
 * @param option
 */
export const getOptionTitle = (option: string | SpecificationOption): string =>
  typeof option === 'string'
    ? option
    : (option.label ?? option.title ?? option.value ?? '');

/**
 * Returns all items from all three catalog item collections.
 * @param catalog
 */
export const getAllItems = (
  catalog: CutsheetCatalog,
): (Cut | Trim | Extra)[] => [
  ...catalog.cuts,
  ...catalog.trim,
  ...catalog.extras,
];

/**
 * Returns all top-level specifications assigned to a specific item via
 * assignedToIds. Does NOT include embedded CatalogSpecifications.
 * @param catalog
 * @param itemId
 */
export const getItemSpecifications = (
  catalog: CutsheetCatalog,
  itemId: string,
): Specification[] =>
  catalog.specifications.filter(
    (spec) => spec.assignedToIds && spec.assignedToIds.includes(itemId),
  );

export type CutsheetSaveType = 'user' | 'vendor' | 'producer' | 'buyer';

/**
 * Returns the save-type options a user can pick on the review step, mirroring
 * the visibility rules of the radio group (processors see vendor/producer,
 * anyone tied to a buyer sees buyer, non-processors see user).
 */
export const getAvailableSaveTypes = (args: {
  isProcessor: boolean;
  producerId: string | undefined;
  buyerId: string | undefined;
}): CutsheetSaveType[] => {
  const { isProcessor, producerId, buyerId } = args;
  const types: CutsheetSaveType[] = [];
  if (isProcessor) types.push('vendor');
  if (isProcessor && producerId) types.push('producer');
  if (buyerId) types.push('buyer');
  if (!isProcessor) types.push('user');
  return types;
};

/**
 * Builds a display name for an item by appending resolved spec values.
 * e.g. "Ribeye Steak/3/4 inch"
 * @param baseTitle
 * @param specs
 * @param specValues
 */
export const buildItemDisplayName = (
  baseTitle: string,
  specs: Specification[],
  specValues: Record<string, string>,
): string => {
  if (specs.length === 0) return baseTitle;

  const specTexts = specs
    .map((spec) => {
      const value = specValues[spec.id] || spec.defaultValue || '';
      if (spec.inputType === 'select' && spec.options) {
        const option = spec.options.find((o) => getOptionValue(o) === value);
        return option ? getOptionTitle(option) : value;
      }
      return value;
    })
    .filter(Boolean);

  return specTexts.length > 0
    ? `${baseTitle}/${specTexts.join('/')}`
    : baseTitle;
};
